import { useState, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface RawRow {
  calendar_year: number;
  calendar_month: number;
  invoice_month_key: number;
  financial_year: number;
  financial_quarter: number;
  financial_year_period: string;
  currency_code: string;
  currency_revenue_usd: string;
  currency_revenue_original: string;
  monthly_grand_total_usd: string;
  pct_contribution_to_month_total: number;
  mom_currency_revenue_change_pct: number;
  yoy_currency_revenue_change_pct: number;
  currency_rank_in_month: number;
  global_invoiced_usd: string;
  global_mom_change_percent: number;
  global_yoy_change_percent: number;
  global_three_month_moving_avg_usd: number;
  global_invoice_count: number;
  global_unique_clients: number;
  distinct_currencies_in_month: number;
  global_prev_month_invoiced_usd: string;
  global_prev_year_invoiced_usd: string;
  global_mom_change_usd: string;
  global_yoy_change_usd: string;
  volatility_level?: string;
  growth_momentum?: string;
  [key: string]: unknown;
}

export interface MonthlyRecord extends RawRow {
  _currencyRows: RawRow[];
}

interface UseCurrencyDashboardResult {
  data: MonthlyRecord[];
  loading: boolean;
  error: string | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useCurrencyDashboard(): UseCurrencyDashboardResult {
  const [data, setData] = useState<MonthlyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const resp = await fetch('/json-data/global_invoiced_revenue_trends.json');
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
        }
        const raw: RawRow[] = await resp.json();

        // Group rows by invoice_month_key
        const grouped = new Map<number, RawRow[]>();
        for (const row of raw) {
          const ym = row.invoice_month_key;
          if (!grouped.has(ym)) grouped.set(ym, []);
          grouped.get(ym)!.push(row);
        }

        // For each month, pick the representative row (lowest currency_rank_in_month)
        const monthly: MonthlyRecord[] = [];
        for (const [, rows] of grouped) {
          const sorted = [...rows].sort((a, b) => a.currency_rank_in_month - b.currency_rank_in_month);
          const rep = sorted[0];
          monthly.push({ ...rep, _currencyRows: rows });
        }

        // Sort chronologically
        monthly.sort((a, b) => a.invoice_month_key - b.invoice_month_key);

        if (!cancelled) {
          setData(monthly);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, []);

  return { data, loading, error };
}

export default useCurrencyDashboard;
